import { useRef, useState } from 'react';
import { bt, btp } from '../../i18n';
import { MediaLibraryModal } from './MediaLibraryModal';
import type { ImageField, ImageValue } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { useFieldTarget } from '../fieldTarget';
import { validateImageFile, imageFromTransfer, ACCEPT_ATTR } from './imageUpload';
import { uploadImage } from '../../lib/uploadAsset';
import {
  cancelMediaUpload,
  localPreviewFor,
  startMediaUpload,
  useMediaUpload,
} from '../../lib/mediaUploads';
import { formatElapsed, useElapsedSeconds } from '../../lib/elapsed';

/**
 * Image control (build-plan §6.3 / §10.6 / project rule): BIBLIOTHÈQUE / UPLOAD / DRAG-DROP / PASTE —
 * there is NEVER a URL text field.
 *
 * Depuis le 05/10/2026 (feature 0070), un clic sur la zone n'ouvre PLUS le sélecteur de fichiers du
 * système : il ouvre la bibliothèque de la boutique, avec les images déjà envoyées, les plus récentes
 * d'abord. Demande de la fondatrice, dans ses mots : « la première popup que je vois c'est cette bib des
 * images déjà uploadées, pour que pas à chaque fois je vais télécharger des images ». Envoyer un fichier
 * neuf reste à un bouton de là, mais ce n'est plus le chemin par défaut — parce que le cas le plus
 * fréquent n'est pas « j'ai une nouvelle image », c'est « je veux CELLE que j'ai déjà envoyée ».
 *
 * Le glisser-déposer et le collage, eux, continuent d'aller DROIT à l'envoi : déposer un fichier est
 * déjà un choix explicite, et lui faire traverser une fenêtre de sélection serait poser une porte
 * devant une porte ouverte. Validates type + size + magic bytes (imageUpload.ts), then UPLOADS the file
 * to `POST /api/assets/images` (spec 0020 Slice 0) and stores the returned delivery URL + the WebP's
 * real width/height for SSR CLS — no more base64 data-URL inline in the document. Carries an optional
 * `alt` (empty means DECORATIVE, which is where the switch starts — or no alt at all, on a `noAlt`
 * field). Clearing sets the value back to undefined.
 *
 * The upload itself does NOT live here (founder, 2026-09-15): it is handed to the background registry
 * (`lib/mediaUploads.ts`), addressed by this field's target, and keeps running — and keeps reporting —
 * when the panel is closed. This component only ever RENDERS that job: mounting on a field with an
 * upload in flight re-attaches to it (same picture, same percentage, same Cancel) instead of showing an
 * empty drop zone, which is what used to send the seller off to re-pick the very file already on its way.
 */
export function ImageControl({
  field,
  value,
  onChange,
}: {
  field: ImageField;
  value: ImageValue | undefined;
  onChange: (next: ImageValue | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  /** Rejections from the CLIENT GATE only (wrong type, too big, not really an image) — instant, local. */
  const [rejected, setRejected] = useState<string | undefined>();
  /**
   * DECORATIVE IS THE DEFAULT (founder, 2026-09-16). A freshly dropped image has no alt text yet, and
   * the panel used to greet it with a red "Add alt text" line — an error face for something the seller
   * had not got wrong. So an empty alt simply MEANS decorative; the switch only leaves that state when
   * the seller turns it off themselves (`wantsAlt`), and an image that already carries alt text comes
   * back with the switch off on its own.
   */
  const [wantsAlt, setWantsAlt] = useState(false);
  /** La bibliothèque est ouverte (feature 0070) — l'écran par défaut d'un clic sur la zone. */
  const [libraryOpen, setLibraryOpen] = useState(false);
  const decorative = !wantsAlt && (value?.alt ?? '').trim() === '';
  const target = useFieldTarget(field.id);
  const job = useMediaUpload(target.key);
  const sending = job !== undefined && job.phase !== 'error';
  const elapsed = useElapsedSeconds(sending ? job.startedAt : undefined);

  const accept = async (file: File | null) => {
    if (!file || sending) return;
    setRejected(undefined);
    try {
      await validateImageFile(file); // cheap client gate before the network round-trip
    } catch (e) {
      setRejected(
        e instanceof Error
          ? e.message
          : bt('chrome.media.image_failed', 'Could not add that image.'),
      );
      return;
    }
    // Everything past this point runs in the registry, not in this component: the seller can close the
    // panel, walk the canvas, open another section — the bytes keep going and the document still gets
    // written when they land.
    // Captured BEFORE the optimistic write below, so the slot can be put back exactly as it was if the
    // upload never lands. The registry decides when — see `revert` in `lib/mediaUploads.ts`.
    const previous = value;
    startMediaUpload({
      key: target.key,
      kind: 'image',
      fileName: file.name,
      reveal: target.reveal,
      revert: () => onChange(previous),
      describeError: (e) =>
        e instanceof Error
          ? e.message
          : bt('chrome.media.image_failed', 'Could not add that image.'),
      run: async (handle) => {
        // The seller's own file, shown from their disk while it travels. The registry owns and revokes it.
        const localUrl = URL.createObjectURL(file);
        handle.ownPreview(localUrl);
        // THE CANVAS TAKES IT NOW (founder, 2026-09-15: "le seller ne vas pas attendre 5 min pour chaque
        // image"). Writing the value only once the upload had landed left the seller composing against an
        // empty slot for the whole trip — and the trip is invisible: `xhr.upload.onprogress` counts bytes
        // handed to the SOCKET, so the bar reads 100 % while the file is still climbing their uplink, and
        // the whole wait hides behind the word "Finishing up…". Their own bytes are already here; there is
        // nothing to wait for. `lib/localMedia.ts` is what keeps this URL out of every save.
        onChange({ url: localUrl, alt: value?.alt ?? '' });
        // Then its geometry, as soon as the browser has decoded the file — off the critical path, so the
        // picture is never held back for it. The ASPECT RATIO is what the canvas lays out on, and that is
        // identical to the stored rendition's, so the delivery URL swaps in later without anything moving.
        // (These numbers never reach the server: the optimistic value is stripped from every save.)
        //
        // `localIsCurrent` closes a real race: on a fast connection the upload can land BEFORE the decode
        // finishes, and a late write would put the `blob:` URL back over the delivery one — an image that
        // dies with the tab, saved as nothing.
        let localIsCurrent = true;
        void intrinsicSize(localUrl).then((size) => {
          if (size && localIsCurrent) onChange({ url: localUrl, alt: value?.alt ?? '', ...size });
        });
        const uploaded = await uploadImage(file, {
          onProgress: handle.setPercent,
          signal: handle.signal,
          onRetry: handle.setRetrying,
        }).finally(() => {
          localIsCurrent = false;
        });
        // File the seller's own bytes under the stored url and hand the job's ownership of them over: the
        // preview then swaps to the delivery URL without ever going dark, and without this tab fetching
        // back the picture it has just sent. (It also ends the job HERE, instead of holding "Sending…" open
        // in the topbar for the seconds a round trip would take.)
        handle.keepPreviewFor(uploaded.url);
        onChange({
          url: uploaded.url,
          alt: value?.alt ?? '',
          width: uploaded.width,
          height: uploaded.height,
        });
        // Fire-and-forget: pulls the stored image into the HTTP cache so the CANVAS repaint behind the
        // panel is instant too. Nothing waits on it — a slow object store must not hold a finished job open.
        void warm(uploaded.url);
      },
    });
  };

  const clear = () => {
    cancelMediaUpload(target.key);
    setRejected(undefined);
    onChange(undefined);
  };

  /**
   * Une image DÉJÀ dans la boutique : rien à envoyer, rien à attendre. L'URL de livraison part telle
   * quelle dans le document, avec les dimensions que le registre connaît (elles évitent le saut de mise
   * en page au rendu serveur). Une entrée rattrapée par la reprise n'a pas de dimensions : on n'en
   * invente pas, on écrit l'URL seule — le navigateur mesure, comme il l'a toujours fait.
   *
   * Le texte alternatif suit l'image : saisi une fois, il est reproposé partout où elle est reprise.
   * Celui déjà posé sur CE champ gagne, parce qu'il a été écrit pour cet emplacement-ci.
   */
  const pickFromLibrary = (asset: {
    url: string;
    alt: string | null;
    width: number | null;
    height: number | null;
  }) => {
    cancelMediaUpload(target.key);
    setRejected(undefined);
    setLibraryOpen(false);
    const alt = (value?.alt ?? '').trim() || (asset.alt ?? '');
    const size =
      asset.width !== null && asset.height !== null
        ? { width: asset.width, height: asset.height }
        : {};
    onChange({ url: asset.url, alt, ...size });
  };

  /** Le bouton « Envoyer un fichier » de la fenêtre : on la ferme et on ouvre le sélecteur système. */
  const requestUpload = () => {
    setLibraryOpen(false);
    inputRef.current?.click();
  };

  const shown = job?.previewUrl ?? localPreviewFor(value?.url) ?? value?.url;
  const error = rejected ?? job?.error;

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} error={error} labelAs="span">
      {/* One flex column so the preview, the alt input and the decorative toggle are SPACED rather than
          glued edge-to-edge — the field slot itself has no layout (same contract as `.bld-link`). */}
      <div className="bld-image">
        {shown ? (
          <div className="bld-image__preview">
            <img src={shown} alt={value?.alt ?? ''} />
            {sending ? (
              <div className="bld-image__sending">
                {/* Two honest pictures, never a made-up number:
                    - nothing measured yet → an INDETERMINATE bar (a sliver crossing the track). Drawing a
                      determinate "0 %" on silence is the same lie as the "100 %" we removed;
                    - measured → the real width, and once the bytes are all in the socket it keeps
                      shimmering, because that phase IS the transfer on a home uplink and a static bar
                      there is exactly what read as "hung". */}
                <span
                  className={
                    job.reported ? 'bld-progress' : 'bld-progress bld-progress--indeterminate'
                  }
                  role="progressbar"
                  aria-valuenow={job.reported ? job.percent : undefined}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={bt('chrome.media.progress', 'Upload progress')}
                >
                  <span
                    className={
                      job.phase === 'processing'
                        ? 'bld-progress__bar bld-progress__bar--waiting'
                        : 'bld-progress__bar'
                    }
                    style={{ inlineSize: job.reported ? `${job.percent}%` : '0%' }}
                  />
                </span>
                <span role="status">
                  {job.retrying > 0 && job.percent < 100
                    ? btp(
                        'chrome.media.retrying',
                        'Connection dropped — retrying ({n}/3)… {pct}%',
                        {
                          n: job.retrying,
                          pct: job.percent,
                        },
                      )
                    : job.phase === 'processing'
                      ? bt('chrome.media.finishing', 'Finishing up…')
                      : job.reported
                        ? btp('chrome.media.sending_pct', 'Sending… {pct}%', { pct: job.percent })
                        : bt('chrome.media.uploading', 'Uploading…')}
                </span>
                {/* The one signal that is always true and always moving — see `lib/elapsed.ts`. Its own
                    element, never glued into a translated sentence, so word order stays the language's. */}
                {elapsed > 0 && (
                  <span className="bld-image__sending-time">{formatElapsed(elapsed)}</span>
                )}
                {/* Naming the promise out loud: the seller who closes this panel mid-upload has no other
                    way to know the file is still on its way. */}
                <span className="bld-image__sending-note">
                  {bt('chrome.media.keeps_going', 'Keeps going if you close this panel.')}
                </span>
                <button
                  type="button"
                  className="bld-btn bld-btn--ghost"
                  onClick={() => cancelMediaUpload(target.key)}
                >
                  {bt('chrome.media.cancel', 'Cancel')}
                </button>
              </div>
            ) : (
              <div className="bld-image__actions">
                {/* « Changer » ouvre la bibliothèque, pas le sélecteur système : remplacer une image
                    par une autre déjà envoyée est le geste le plus fréquent sur un emplacement rempli,
                    et il n'avait aucun bouton — il fallait vider puis re-choisir. */}
                <button
                  type="button"
                  className="bld-iconbtn bld-image__change"
                  onClick={() => setLibraryOpen(true)}
                >
                  {bt('chrome.media.change', 'Change')}
                </button>
                <button type="button" className="bld-iconbtn bld-image__clear" onClick={clear}>
                  {bt('chrome.media.remove', 'Remove')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
            className={over ? 'bld-image__drop is-over' : 'bld-image__drop'}
            role="button"
            tabIndex={0}
            aria-label={btp('chrome.media.upload_aria', 'Upload {name}', { name: field.label })}
            aria-busy={sending}
            onClick={() => !sending && setLibraryOpen(true)}
            onKeyDown={(e) => {
              if (!sending && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setLibraryOpen(true);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!sending) setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void accept(
                imageFromTransfer(e.dataTransfer.items) ?? e.dataTransfer.files[0] ?? null,
              );
            }}
            onPaste={(e) => void accept(imageFromTransfer(e.clipboardData.items))}
          >
            {sending ? (
              <strong>{bt('chrome.media.uploading', 'Uploading…')}</strong>
            ) : (
              <>
                <strong>{bt('chrome.media.choose_image', 'Choose an image')}</strong>
                <span>
                  {bt(
                    'chrome.media.image_hint_library',
                    'Click to pick from your images, drag & drop, or paste',
                  )}
                </span>
              </>
            )}
          </div>
        )}

        {/* A failure that happened while this panel was closed is waiting here with the bytes still in
            hand — one button, no second trip through the OS file browser. */}
        {job?.phase === 'error' && (
          <button type="button" className="bld-btn bld-btn--ghost" onClick={() => job.retry()}>
            {bt('chrome.media.try_again', 'Try again')}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          style={{ display: 'none' }}
          onChange={(e) => {
            void accept(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />

        {/* La bibliothèque (feature 0070). Montée seulement quand elle est ouverte : la liste se
            recharge donc à chaque ouverture, ce qui est voulu — une image envoyée depuis un autre
            champ il y a dix secondes doit être là, et un cache qui survit à la fermeture serait
            exactement la raison pour laquelle elle n'y serait pas. */}
        {libraryOpen && (
          <MediaLibraryModal
            kind="IMAGE"
            onPick={pickFromLibrary}
            onUploadRequested={requestUpload}
            onClose={() => setLibraryOpen(false)}
          />
        )}

        {/* `noAlt` fields carry no meaning to read out (the favicon: a tab shows an icon, never a
            description), so they get no alt input, no decorative switch and no missing-alt reminder. */}
        {value && !field.noAlt && (
          <>
            <input
              className="bld-input"
              type="text"
              aria-label={bt('chrome.media.alt', 'Alt text')}
              placeholder={bt('chrome.media.alt_ph', 'Describe this image (alt text)')}
              value={value.alt}
              disabled={decorative}
              onChange={(e) => onChange({ ...value, alt: e.target.value })}
            />
            <button
              type="button"
              className="bld-switch"
              role="switch"
              aria-checked={decorative}
              aria-label={bt('chrome.media.decorative', 'Decorative image (no alt text)')}
              onClick={() => {
                const next = !decorative;
                setWantsAlt(!next);
                if (next) onChange({ ...value, alt: '' });
              }}
            >
              <span className="bld-switch__track" aria-hidden="true">
                <span className="bld-switch__knob" />
              </span>
              <span style={{ fontSize: 'var(--aqx-text-2xs)' }}>
                {bt('chrome.media.decorative_short', 'Decorative (no alt)')}
              </span>
            </button>
            {/* Advisory, never an error: the seller asked for the description themselves by turning the
                switch off, so this only reminds them what it is for — in the muted note type, not in red. */}
            {!decorative && value.alt.trim() === '' && (
              <p className="bld-field__hint">
                {bt(
                  'chrome.media.alt_hint',
                  'Describe the image for people who use a screen reader.',
                )}
              </p>
            )}
          </>
        )}
      </div>
    </FieldRow>
  );
}

/**
 * Pull the stored image into the browser cache, giving up after [WARM_TIMEOUT_MS]. Never rejects and
 * nobody awaits it: it is a comfort step for the canvas repaint, not a gate on the upload.
 */
/**
 * The pixel size of a picked file, read from the browser's own decode. `undefined` when it cannot be read
 * (a corrupt file, a format this browser will not decode) — the canvas then lays the picture out without a
 * declared ratio for the few seconds until the delivery URL arrives with the authoritative numbers.
 *
 * Deliberately NOT a second call into `@aquilixshop/media`: the shrink already decodes the file for its own
 * purposes, but it does so inside `uploadImage`, and reaching in there to hand the size back would couple
 * this control to the uploader's internals for a number an `<img>` gives away for free.
 */
function intrinsicSize(url: string): Promise<{ width: number; height: number } | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve(
        img.naturalWidth > 0 && img.naturalHeight > 0
          ? { width: img.naturalWidth, height: img.naturalHeight }
          : undefined,
      );
    img.onerror = () => resolve(undefined);
    img.src = url;
  });
}

function warm(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    const done = () => resolve();
    img.onload = done;
    img.onerror = done;
    img.src = url;
    setTimeout(done, WARM_TIMEOUT_MS);
  });
}

const WARM_TIMEOUT_MS = 3_000;
